import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import { isEmpty } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { emptyArray } from 'ui-components/default-props/empty';
import { ItemDisplayImage } from './item-image.component';
import { ItemName } from './item-name';
import { ViewDescription } from './view-description';

import { testId } from 'ui-components/services/testid-service';
import { Button } from 'ui-components/components/button';

import { DangerousGoodsTag } from '../../dangerous-goods/dangerous-goods-tag/dangerous-goods-tag';

import style from '../style.module.less';
import { LineItemAdjustmentNotice } from './line-item-adjustment-notice';
import { noop } from 'ui-components/default-props/noop';

const itemDisplayDescriptions = (descriptions) => {
  if (isEmpty(descriptions)) return null;

  const ignoredItemOptions = ['CustomizedURL'];

  return (
    <div className={style['item-descriptions']}>
      {descriptions.map(({ name, value }, index) => {
        if (ignoredItemOptions.includes(name)) return null;
        return (
          <div key={index} className={style.description}>
            <b>{name}:</b>
            <span>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

export const ItemDisplay = ({
  originalQuantity = null,
  quantity = 0,
  layout = 'regular',
  showImage = true,
  className = '',
  name = '',
  sku = '',
  attributes = emptyArray,
  productThumbnailUrl = '',
  onNameClicked,
  productId,
  onBundleClick = noop,
  bundleProductId,
  bundleProductSku,
  isClassifiedAsDangerousGoods = false,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);

  const toggleDescription = useCallback(() => {
    setIsDescriptionExpanded((prevState) => !prevState);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isDescriptionExpanded, setIsDescriptionExpanded]);

  const hasAdjustments = quantity !== originalQuantity && originalQuantity !== null;

  return (
    <div
      className={classnames(style['item-display'], style[layout], className, {
        [style['no-image']]: !showImage,
        [style['no-attributes']]: !attributes,
      })}
    >
      {showImage && <ItemDisplayImage name={name} productThumbnailUrl={productThumbnailUrl} />}
      <div className={style['non-image-content']}>
        <div className={style['text-content']}>
          <ItemName name={name} onNameClicked={onNameClicked} productId={productId} />
          <div className={style['item-sku']}>
            <FormattedMessage defaultMessage='SKU:' description='item.sku' id='bzG1b9' />
            &nbsp;<span {...testId('item-sku-display')}>{sku}</span>
          </div>
          {isClassifiedAsDangerousGoods ? <DangerousGoodsTag className={style['dangerous-goods-tag']} /> : null}
          {bundleProductId && (
            <div className={style['item-sku']}>
              <FormattedMessage defaultMessage='Bundle SKU:' description='line item display, bundle sku' id='YtkfGs' />
              &nbsp;
              <Button onClick={onBundleClick} theme='link'>
                {bundleProductSku}
              </Button>
            </div>
          )}
          {hasAdjustments && <LineItemAdjustmentNotice />}
          {Array.isArray(attributes) && attributes.length > 0 && (
            <ViewDescription isDescriptionExpanded={isDescriptionExpanded} toggleDescription={toggleDescription} />
          )}
          {isDescriptionExpanded && itemDisplayDescriptions(attributes)}
        </div>
      </div>
    </div>
  );
};

ItemDisplay.propTypes = {
  layout: PropTypes.oneOf(['regular', 'small', 'hidePriceAndQuantity']),
  productId: PropTypes.number,
  showImage: PropTypes.bool,
  name: PropTypes.string,
  sku: PropTypes.string,
  productThumbnailUrl: PropTypes.string,
  attributes: PropTypes.array,
  className: PropTypes.string,
  onNameClicked: PropTypes.func.isRequired,
  originalQuantity: PropTypes.number,
  quantity: PropTypes.number,
  bundleProductId: PropTypes.number,
  bundleProductSku: PropTypes.string,
  onBundleClick: PropTypes.func,
  isClassifiedAsDangerousGoods: PropTypes.bool,
};
